import React, { type FC, type PropsWithChildren } from 'react'
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    ListItem,
    ListItemText,
    type SxProps,
    type Theme,
    Typography,
} from '@mui/material'

import { ConnectwareError, isCybusCatalogApplicationCategory, Translation } from '../../../../domain'

import { DocumentationType } from '../../../../application'

import { useAsyncCallback } from '../../Callback'
import { Button } from '../../common'
import { DocumentationLink } from '../../Documentation'
import { ErrorMessage } from '../../ErrorMessage'
import { createFormatter, FormattedTranslation } from '../../Internationalization'
import { useAppUsecase } from '../../State'
import { ServiceCard } from './Card'
import { useCategoryTranslation, useFilteredApplications, useTemplate } from './Hooks'

const howToParagraphStyle: SxProps<Theme> = { mb: 2 }
const templatesStyle: SxProps<Theme> = { display: 'flex' }
const actionsStyle: SxProps<Theme> = { '& > *': { my: 'auto' } }
const errorStyle: SxProps<Theme> = { mr: 'auto' }

const Paragraph: FC<PropsWithChildren> = ({ children }) => (
    <ListItem>
        <ListItemText secondary={<Typography>{children}</Typography>} />
    </ListItem>
)

export const TemplateDialog: FC = () => {
    const translateCategory = useCategoryTranslation()

    const downloadServiceCatalogTemplateUsecase = useAppUsecase('downloadServiceCatalogTemplateUsecase')

    const filtered = useFilteredApplications()
    const template = useTemplate()

    const [download, loading, value] = useAsyncCallback(() => downloadServiceCatalogTemplateUsecase.download(), [downloadServiceCatalogTemplateUsecase])

    if (template === null) {
        return <></>
    }

    const isEnum = isCybusCatalogApplicationCategory(template)

    const category = isEnum ? template : template.category
    const close: VoidFunction = () => downloadServiceCatalogTemplateUsecase.close()

    return (
        <Dialog data-testid="service-catalog-template-dialog" fullWidth disableEscapeKeyDown onClose={close} maxWidth="md" open>
            <DialogTitle>
                <FormattedTranslation id={Translation.CREATE_YOUR_OWN_SERVICE} />
            </DialogTitle>
            {loading ? <LinearProgress variant="indeterminate" /> : <LinearProgress variant="determinate" value={0} />}
            <DialogContent>
                <Box data-testid="template-how-to" sx={howToParagraphStyle}>
                    <Typography variant="h6">
                        <FormattedTranslation id={Translation.HOW_TO} />
                    </Typography>
                    <FormattedTranslation
                        id={Translation.TEMPLATE_COMMISSIONING_FILE_DOWNLOAD_HOW_TO}
                        values={{
                            p: createFormatter(Paragraph, {}),
                            commissioningFileLink: createFormatter(DocumentationLink, { type: DocumentationType.SERVICES_COMMISSIONING_FILE }),
                            cybusLearnLink: createFormatter(DocumentationLink, { type: DocumentationType.SERVICES }),
                        }}
                    />
                </Box>
                <Typography variant="h6">
                    <FormattedTranslation id={Translation.TEMPLATES_FROM_CATEGORY} values={{ category: translateCategory(category) }} />
                </Typography>
                <Box sx={templatesStyle}>
                    {filtered.getTemplates(category).map((service, k) => (
                        <ServiceCard
                            key={k}
                            service={service}
                            selected={template === service}
                            onClick={() => downloadServiceCatalogTemplateUsecase.selectTemplate(service)}
                        />
                    ))}
                </Box>
            </DialogContent>
            <DialogActions sx={actionsStyle}>
                {!loading && ConnectwareError.is(value) && (
                    <ErrorMessage data-testid="service-catalog-template-error" sx={errorStyle} titleVariant="body2" error={value} />
                )}
                <Button data-testid="close" variant="secondary" onClick={close}>
                    <FormattedTranslation id={Translation.CLOSE} />
                </Button>
                <Button data-testid="download" variant="primary" onClick={download} disabled={isEnum}>
                    <FormattedTranslation id={Translation.DOWNLOAD_TEMPLATE} />
                </Button>
            </DialogActions>
        </Dialog>
    )
}
